import { Button, Switch } from '@mantine/core'
import clsx from 'clsx'
import moment from 'moment'
import { Icon } from '../gsys-ui'
import { UsersIcon } from '@heroicons/react/24/outline'
import useApi from '../hooks/useApi'
import { useState } from 'react'
import { UserIcon } from 'lucide-react'
import haxios from '../lib/haxios'
import { errorToast, successToast } from '../lib/helpers'

const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

const HolidayReviewModal = ({ context, id, innerProps }) => {
  const { thisReq, requests, refetch } = innerProps
  const [showOnlyHoliday, setShowOnlyHoliday] = useState(true)
  const [reviewNotes, setReviewNotes] = useState('')

  const { data: ustafferators, isLoading: isLoadingUstaff } = useApi({
    url: `/salesassistnew/ustafferators`
  })

  const dayCount = moment(thisReq.dtEnd).diff(moment(thisReq.dtStart), 'days')
  const middleDay = moment(thisReq.dtStart).add(Math.floor(dayCount / 2), 'days')
  const rangeStart = moment(middleDay).subtract(10, 'days')

  // Filter out rejected requests and group remaining by staffId
  const validRequests = requests.filter((req) => req.Status !== 'REJECTED')
  const requestsByStaff = validRequests.reduce((acc, req) => {
    if (!acc[req.staffId]) {
      acc[req.staffId] = []
    }
    acc[req.staffId].push(req)
    return acc
  }, {})

  if (isLoadingUstaff) return null

  // Filter staff based on showOnlyHoliday
  const filteredStaff = showOnlyHoliday
    ? ustafferators.filter((staff) => requestsByStaff[staff.StaffId]?.length > 0 || staff.StaffId === thisReq.staffId)
    : ustafferators

  // Group staff by position
  const staffByPosition = filteredStaff
    .filter((staff) => staff.StaffId !== thisReq.staffId)
    .reduce((acc, staff) => {
      const position = staff.Position || 'Other'
      if (!acc[position]) {
        acc[position] = []
      }
      acc[position].push(staff)
      return acc
    }, {})

  const handleApprove = async () => {
    try {
      const holidayId = Date.now().toString(36) + Math.random().toString(36).substr(2, 5)
      const totalDays = moment(thisReq.dtEnd).diff(moment(thisReq.dtStart), 'days') + 1

      await haxios.put(`/salesassist/cplusassist/holidayrequests/${thisReq._id}`, {
        ...thisReq,
        decision: 'APPROVED',
        Notes: reviewNotes
      })

      const res = await haxios.post('https://api.aa-vision.com/api/tma/holiday', {
        HolidayID: holidayId,
        StartDatetime: thisReq.dtStart,
        EndDatetime: thisReq.dtEnd,
        StaffID: thisReq.staffId,
        TotalDays: totalDays,
        Paid: true,
        HoursPerDay: 8,
        AddedUserID: '988cca25-74f8-4908-ab96-f2ac9be8d9b3',
        AddedDatetime: moment().format('YYYY-MM-DD HH:mm:ss')
      })

      await refetch()
      successToast('Holiday approved and booked successfully')
      context.closeModal(id)
    } catch (error) {
      console.error('Error during holiday approval and booking:', error)
      errorToast(error.message)
    }
  }

  const handleReject = async () => {
    try {
      await haxios.put(`/salesassist/cplusassist/holidayrequests/${thisReq._id}`, {
        ...thisReq,
        decision: 'REJECTED',
        Notes: reviewNotes
      })

      await refetch()
      successToast('Holiday has been rejected successfully')
      context.closeModal(id)
    } catch (error) {
      console.error('Error during holiday approval and booking:', error)
      errorToast(error.message)
    }
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-none items-start p-4 border-b-2 border-b-gray-200">
        <div className="flex-none">
          <div className="text-xl font-semibold">{thisReq.StaffName}</div>
          <div className="">
            {thisReq.Position} - {thisReq.Branch}
          </div>
          <div className="">
            {moment(thisReq.dtStart).format('DD/MM/YYYY')} - {moment(thisReq.dtEnd).format('DD/MM/YYYY')}{' '}
            <span className="text-sm">({dayCount} days)</span>
          </div>
          <div className="">Reason: {thisReq.reason}</div>
        </div>
        <div className="flex flex-1 justify-end items-start items-center space-x-2 select-none">
          <div className="px-3 py-1 text-sm font-semibold text-white bg-blue-500 rounded-full">This request</div>
          <div className="px-3 py-1 text-sm font-semibold text-white bg-orange-500 rounded-full">Pending request</div>
          <div className="px-3 py-1 text-sm font-semibold text-white bg-green-500 rounded-full">Approved holiday</div>
          <Switch
            checked={showOnlyHoliday}
            onChange={(event) => setShowOnlyHoliday(event.currentTarget.checked)}
            label="Show only holiday"
          />
        </div>
      </div>
      <div className="overflow-hidden flex-1">
        <div className="h-[500px] overflow-y-auto">
          <div className="sticky top-0 z-50 bg-white border-b shadow-sm border-b-gray-200">
            <div className="flex sticky top-0 z-50 font-semibold text-center bg-white border-b-2 border-b-gray-200">
              <div className="flex flex-1 items-center px-2 space-x-1 border-r-2 border-r-gray-200">
                <Icon Comp={UserIcon} />
                <div>Requesting staff</div>
              </div>
              <div className="flex flex-none">
                {Array.from({ length: 21 }, (_, ind) => {
                  const thisDate = moment(rangeStart).add(ind, 'days')
                  const isToday = moment().isSame(thisDate, 'day')
                  const dayOfWeek = daysOfWeek[thisDate.day()]
                  const isWeekend = dayOfWeek === 'S'

                  return (
                    <div
                      className={clsx(
                        'pt-0.5 w-8 border-r border-r-gray-200 z-10 relative',
                        isWeekend && 'bg-gray-100'
                      )}
                      key={ind}
                    >
                      <div className="leading-tight">{thisDate.date()}</div>
                      <div className="text-sm font-normal leading-tight">{dayOfWeek}</div>
                      <div
                        className={clsx(
                          'absolute top-[1px] right-[1px] bottom-[1px] left-[1px] pointer-events-none',
                          isToday && 'outline outline-2 outline-yellow-400 z-50'
                        )}
                      />
                    </div>
                  )
                })}
              </div>
            </div>
            <StaffRow
              thisReq={thisReq}
              staffName={thisReq.StaffName}
              requests={[thisReq, ...(requestsByStaff[thisReq.staffId] || [])]}
              dayCount={21}
              startDate={rangeStart}
            />
          </div>

          <div className="z-40 mt-4">
            {Object.entries(staffByPosition).map(([position, staffList]) => (
              <div key={position}>
                <div className="flex items-center px-2 py-2 space-x-1 font-semibold text-gray-700 bg-gray-100 border-t border-b border-gray-200">
                  <Icon Comp={UsersIcon} />
                  <div>{position}</div>
                </div>
                {staffList.map((staff) => (
                  <StaffRow
                    thisReq={thisReq}
                    key={staff.StaffId}
                    staffName={`${staff.FName} ${staff.SName}`}
                    requests={requestsByStaff[staff.StaffId] || []}
                    dayCount={21}
                    startDate={rangeStart}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 bg-gray-200 border-t-2 border-gray-300">
          <div className="mb-2 font-semibold text-gray-700">Review Notes</div>
          <textarea
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            className="p-2 mb-4 w-full h-24 rounded-md border border-gray-300 resize-none"
            placeholder="Enter your review notes here..."
          />
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => context.closeModal(id)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleReject} disabled={!!!reviewNotes}>
              Reject
            </Button>
            <Button color="green" onClick={handleApprove} disabled={!!!reviewNotes}>
              Accept
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

const StaffRow = ({ thisReq, staffName, requests, dayCount, startDate }) => {
  return (
    <div className="flex h-full">
      <div className="flex-1 px-2 py-1 border-r-2 border-b border-r-gray-200 border-b-gray-200 h-[63px] relative z-40">
        <div className="flex justify-end items-center h-full text-lg font-bold">{staffName}</div>
      </div>

      <div className="flex-none p-0 h-[63px] relative">
        {Array.from({ length: dayCount }, (_, ind) => {
          const thisDate = moment(startDate).add(ind, 'days')
          const dayStart = thisDate.startOf('day')
          const dayEnd = thisDate.endOf('day')

          const holidayRequest = requests.find((req) => {
            const reqStart = moment(req.dtStart).startOf('day')
            const reqEnd = moment(req.dtEnd).endOf('day')
            return (
              dayStart.isBetween(reqStart, reqEnd, undefined, '[]') ||
              dayEnd.isBetween(reqStart, reqEnd, undefined, '[]') ||
              reqStart.isBetween(dayStart, dayEnd, undefined, '[]')
            )
          })

          const isHoliday = !!holidayRequest && holidayRequest?.decision !== 'REJECTED'
          const isApproved = holidayRequest?.decision === 'APPROVED'
          const isPending =
            holidayRequest && holidayRequest._id !== thisReq._id && holidayRequest?.decision !== 'REJECTED'

          console.log(isPending, holidayRequest)

          return (
            <div
              key={ind}
              className="inline-block relative w-8 h-full border-r border-b cursor-pointer border-r-gray-200 border-b-gray-200"
            >
              <div
                className={clsx(
                  'absolute inset-0 flex flex-col items-center justify-center overflow-hidden text-white',
                  isHoliday && (isApproved ? 'bg-green-500' : isPending ? 'bg-orange-400' : 'bg-blue-500')
                )}
              >
                {!isApproved && !isPending && 'R'}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default HolidayReviewModal
